import React, { useState } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import BatchManagement from "./BatchManagement";
import StudentManagement from "./StudentManagement";
import ResourceManagement from "./ResourceManagement";
import TopicManagement from "./TopicManagement";
import Payment from "./Payment";
import MockAndTests from "./MockAndTests";
import Marks from "./Marks";
import HomepageManagement from "./HomepageManagement";
import Homework from "./Homework";

import { auth } from "../../firebase";
import { signOut } from "firebase/auth";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("batches");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar toggle button (persistent, floating) */}
      <button
        className="fixed bottom-4 left-4 z-50 bg-blue-800 text-white p-3 rounded-full shadow-lg focus:outline-none md:hidden"
        aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
        onClick={() => setSidebarOpen((open) => !open)}
      >
        {sidebarOpen ? (
          // X icon
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          // Hamburger icon
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        )}
      </button>
      <div className="flex h-screen">
        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
        <div
          className={`w-64 bg-slate-900 text-white p-4 flex flex-col z-40 fixed md:static h-full top-0 left-0 transition-transform duration-300 md:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          }`}
          style={{ minHeight: "100vh" }}
        >
          {/* Close button on mobile */}
          <div className="flex md:hidden justify-end mb-2">
            <button
              className="text-white p-2 rounded-full hover:bg-slate-800 focus:outline-none"
              aria-label="Close sidebar"
              onClick={() => setSidebarOpen(false)}
            >
              {/* X icon */}
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <h2 className="text-2xl font-bold text-center mt-2 mb-2">
            Admin Panel
          </h2>
          <hr className="w-full border-t border-slate-200/30 mb-4" />
          <nav>
            <ul className="space-y-2">
              <li>
                <Link
                  to="batches"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "batches"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("batches");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Clipboard List */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5h6M9 3h6a2 2 0 012 2v14a2 2 0 01-2 2H9a2 2 0 01-2-2V5a2 2 0 012-2z"
                    />
                  </svg>
                  Batches
                </Link>
              </li>
              <li>
                <Link
                  to="students"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "students"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("students");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Users */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m9-5a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  Students
                </Link>
              </li>
              <li>
                <Link
                  to="resources"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "resources"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("resources");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Collection */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 11H5m14 4H5m7-8v12"
                    />
                  </svg>
                  Resources
                </Link>
              </li>
              <li>
                <Link
                  to="topics"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "topics" ? "bg-blue-600/90" : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("topics");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Bookmark */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 5v14l7-7 7 7V5a2 2 0 00-2-2H7a2 2 0 00-2 2z"
                    />
                  </svg>
                  Topics
                </Link>
              </li>
              <li>
                <Link
                  to="payment"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "payment"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("payment");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Dollar Sign */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 2v20m6-10a6 6 0 11-12 0 6 6 0 0112 0z"
                    />
                  </svg>
                  Payment
                </Link>
              </li>
              <li>
                <Link
                  to="mock-and-tests"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "mock-and-tests"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("mock-and-tests");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Clipboard List */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5h6M9 3h6a2 2 0 012 2v14a2 2 0 01-2 2H9a2 2 0 01-2-2V5a2 2 0 012-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9h6M9 13h6M9 17h6" />
                  </svg>
                  Mock and Tests
                </Link>
              </li>
              <li>
                <Link
                  to="marks"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "marks"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("marks");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Academic Cap */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0v6m0 0H6m6 0h6" />
                  </svg>
                  Marks
                </Link>
              </li>
              <li>
                <Link
                  to="homework"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "homework"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("homework");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Clipboard with Check */}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7 7h10a2 2 0 012 2v9a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z" />
                  </svg>
                  Homework
                </Link>
              </li>
              <li>
                <Link
                  to="homepage"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold hover:bg-blue-600/80 focus:bg-blue-700/90 ${
                    activeTab === "homepage"
                      ? "bg-blue-600/90"
                      : "bg-blue-800/40"
                  }`}
                  onClick={() => {
                    setActiveTab("homepage");
                    if (window.innerWidth < 768) setSidebarOpen(false);
                  }}
                >
                  {/* Icon: Home */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Homepage
                </Link>
              </li>
            </ul>
            <hr className="mt-16 w-full border-t border-slate-200/30 mb-4" />
            <button
              onClick={handleLogout}
              className=" w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold shadow flex items-center justify-center gap-2"
            >
              {/* Icon: Logout */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1"
                />
              </svg>
              Logout
            </button>
          </nav>
        </div>
        {/* Main Content */}
        <div
          className="flex-1 p-8 overflow-y-auto"
          onClick={() => sidebarOpen && setSidebarOpen(false)}
        >
          <Routes>
            <Route path="batches" element={<BatchManagement />} />
            <Route path="students" element={<StudentManagement />} />
            <Route path="resources" element={<ResourceManagement />} />
            <Route path="topics" element={<TopicManagement />} />
            <Route path="payment" element={<Payment />} />
<Route path="mock-and-tests" element={<MockAndTests />} />
<Route path="marks" element={<Marks />} />
            <Route path="homepage" element={<HomepageManagement />} />
            <Route path="homework" element={<Homework />} />

            <Route index element={<BatchManagement />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
